import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Household } from '@models/foyers/household.model';

@Component({
  selector: 'item-delivery-round-household',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './item-delivery-round-household.component.html',
})
export class ItemDeliveryRoundHouseholdComponent
  implements OnChanges, OnDestroy
{
  @Input({ required: true })
  household!: Household;

  @Input({ required: true })
  position!: number;

  @Input({ required: true })
  color: 'bleu' | 'rose' = 'bleu';

  @Input()
  isFirst: boolean = false;

  @Input()
  isLast: boolean = false;

  @Input()
  totalHouseholds: number = 0;

  @Output()
  dragStarted = new EventEmitter<{ household: Household; position: number }>();

  @Output()
  dragEnded = new EventEmitter<void>();

  @Output()
  dropped = new EventEmitter<{
    household: Household;
    targetPosition: number;
  }>();

  @Output()
  positionChanged = new EventEmitter<{
    household: Household;
    targetPosition: number;
  }>();

  isDragging = false;
  showDropIndicator: 'before' | 'after' | null = null;
  showPositionEditor = false;

  _inputPosition = new FormControl<number | null>(null, {
    validators: [Validators.required, Validators.min(1)],
  });

  /**
   * Met à jour le validateur max lorsque totalHouseholds change
   */
  ngOnChanges(): void {
    // On ne valide que le minimum (1), pas de maximum
    this._inputPosition.setValidators([Validators.required, Validators.min(1)]);
    this._inputPosition.updateValueAndValidity();
  }

  onDragStart(event: DragEvent): void {
    this.isDragging = true;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData(
        'text/plain',
        JSON.stringify({
          householdId: this.household.id,
          position: this.position,
        })
      );
    }
    this.dragStarted.emit({
      household: this.household,
      position: this.position,
    });
  }

  onDragEnd(event: DragEvent): void {
    this.isDragging = false;
    this.showDropIndicator = null;
    this.dragEnded.emit();
  }

  /**
   * Gère le dragover sur une zone de drop (avant ou après)
   */
  onDropZoneDragOver(event: DragEvent, position: 'before' | 'after'): void {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }

    this.showDropIndicator = position;
  }

  /**
   * Gère le dragleave sur une zone de drop
   */
  onDropZoneDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.showDropIndicator = null;
  }

  /**
   * Gère le drop sur une zone (avant ou après)
   */
  onDropZoneDrop(event: DragEvent, position: 'before' | 'after'): void {
    event.preventDefault();
    event.stopPropagation();

    this.showDropIndicator = null;

    if (event.dataTransfer) {
      const data = JSON.parse(event.dataTransfer.getData('text/plain'));
      if (data.householdId !== this.household.id) {
        // Calculer la position cible
        let targetPosition = this.position;

        // Si on drop APRÈS, on veut insérer après cet élément
        if (position === 'after') {
          targetPosition = this.position + 1;
        }
        // Si on drop AVANT, on insère à la position actuelle

        this.dropped.emit({ household: this.household, targetPosition });
      }
    }
  }

  /**
   * Ouvre l'éditeur de position inline
   */
  openPositionEditor(): void {
    if (this.showPositionEditor) {
      this.showPositionEditor = false;
      return;
    }

    this.showPositionEditor = true;
    this._inputPosition.setValue(this.position + 1); // +1 car affichage en 1-indexed

    // Focus sur l'input après un court délai pour laisser le temps au DOM de se mettre à jour
    setTimeout(() => {
      const input = document.querySelector(
        '.position-editor-input'
      ) as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  }

  /**
   * Annule l'édition de position
   */
  cancelPositionEdit(): void {
    this.showPositionEditor = false;
    this._inputPosition.reset();
  }

  /**
   * Valide le changement de position
   */
  validatePositionChange(): void {
    if (this._inputPosition.invalid || this._inputPosition.value === null) {
      return;
    }

    let newPosition = this._inputPosition.value;

    // Vérifier que la position est au moins 1
    if (newPosition < 1) {
      return;
    }

    // Si la position dépasse le nombre total de foyers, placer à la fin
    if (newPosition > this.totalHouseholds) {
      newPosition = this.totalHouseholds;
    }

    // Convertir en 0-indexed pour l'API
    const targetIndex = newPosition - 1;

    // Toujours émettre l'événement, la méthode parent vérifiera si la position a vraiment changé
    this.positionChanged.emit({
      household: this.household,
      targetPosition: targetIndex,
    });

    this.cancelPositionEdit();
  }

  /**
   * Gère les clics sur le document pour fermer l'éditeur si on clique en dehors
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showPositionEditor) {
      return;
    }

    const target = event.target as HTMLElement;

    // Vérifier si le clic est sur l'éditeur ou sur le bouton qui l'ouvre
    const isClickInsideEditor = target.closest('.position-editor-container');
    const isClickOnOpenButton = target.closest('.position-open-button');

    if (!isClickInsideEditor && !isClickOnOpenButton) {
      this.cancelPositionEdit();
    }
  }

  /**
   * Nettoyage lors de la destruction du composant
   */
  ngOnDestroy(): void {
    // Pas de nettoyage nécessaire car HostListener se désabonne automatiquement
  }
}
